import React, { useState, useRef, useEffect } from 'react';
import { storage } from '../firebase'; // Asegúrate de que la ruta sea correcta
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import CSSFilter from 'react-css-filter'; // Librería para aplicar filtros al video

const Uploadh = () => {
  const [videoFile, setVideoFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadURL, setDownloadURL] = useState('');
  const [error, setError] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [mediaStream, setMediaStream] = useState(null);
  const [countdown, setCountdown] = useState(40);
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [videoSent, setVideoSent] = useState(false);

  const videoPreviewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingTimeoutRef = useRef(null);

  // Inicia la cuenta regresiva de 40 segundos al comenzar la grabación
  useEffect(() => {
    let timer;
    if (isRecording) {
      setCountdown(40);
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isRecording]);

  // Manejar la selección de archivo y validar la duración (máx 40 s)
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      setError('Por favor, sube un archivo de video.');
      return;
    }

    const videoURL = URL.createObjectURL(file);
    const videoElement = document.createElement('video');
    videoElement.preload = 'metadata';
    videoElement.src = videoURL;
    videoElement.onloadedmetadata = () => {
      URL.revokeObjectURL(videoURL);
      if (videoElement.duration > 40) {
        setError('El video es demasiado largo. La duración máxima es de 40 segundos.');
      } else {
        setError('');
        setVideoFile(file);
      }
    };
  };

  // Iniciar la grabación utilizando el stream activo de la cámara
  const startRecording = () => {
    if (!mediaStream) {
      setError('La cámara no está activa.');
      return;
    }
    const options = { mimeType: 'video/webm' };
    const recorder = new MediaRecorder(mediaStream, options);
    mediaRecorderRef.current = recorder;
    const chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunks.push(e.data);
      }
    };
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: 'video/webm' });
      const file = new File([blob], 'recorded-video.webm', { type: 'video/webm' });
      setVideoFile(file);
      setRecordedChunks(chunks);
      // Detener el stream de la cámara
      mediaStream.getTracks().forEach((track) => track.stop());
      setMediaStream(null);
      setIsCameraActive(false);
      setIsRecording(false);
    };
    recorder.start();
    setIsRecording(true);
    // Detener la grabación automáticamente después de 40 s
    recordingTimeoutRef.current = setTimeout(() => {
      stopRecording();
    }, 40000);
  };

  // Detener la grabación manualmente
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
    }
  };

  // Subir el video a Firebase Storage
  const handleUpload = () => {
    if (!videoFile) {
      setError('No has seleccionado ningún video.');
      return;
    }
    const storageRef = ref(storage, `videos/${videoFile.name}`);
    const uploadTask = uploadBytesResumable(storageRef, videoFile);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        setError(error.message);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((url) => {
          setDownloadURL(url);
        });
      }
    );
  };

  // Eliminar el video de Firebase Storage y reiniciar estados
  const handleDelete = () => {
    if (!videoFile) return;
    const videoRef = ref(storage, `videos/${videoFile.name}`);
    deleteObject(videoRef)
      .then(() => {
        setDownloadURL('');
        setVideoFile(null);
        setUploadProgress(0);
        setError('');
        setVideoSent(false);
      })
      .catch((error) => {
        setError('Error al eliminar el video: ' + error.message);
      });
  };

  // Al presionar "Enviar", se marca el video como enviado y se muestra el mensaje de confirmación
  const handleSend = () => {
    setVideoSent(true);
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Sube tu Video</h1>
      <p style={styles.subtitle}>
        Envía tus saludos y lo estaremos colocando en vivo
      </p>

      <div style={styles.buttonGroup}>
        {/* Botón para seleccionar archivo */}
        <label htmlFor="fileInput" style={styles.buttonPrimary}>
          Seleccionar Archivo
        </label>
        <input
          id="fileInput"
          type="file"
          accept="video/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>

      {/* Vista previa en vivo con filtro si la cámara está activa y no se está grabando */}
      {isCameraActive && !isRecording && (
        <div style={styles.previewContainer}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Filtro:</label>
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              style={styles.select}
            >
              <option value="none">Ninguno</option>
              <option value="grayscale(100%)">Grayscale</option>
              <option value="sepia(100%)">Sepia</option>
              <option value="invert(100%)">Invert</option>
              <option value="blur(5px)">Blur</option>
              <option value="brightness(150%)">Brightness</option>
            </select>
          </div>
          <CSSFilter filter={selectedFilter}>
            <video
              ref={videoPreviewRef}
              autoPlay
              muted
              style={styles.videoPreview}
            />
          </CSSFilter>
          <button onClick={startRecording} style={styles.buttonPrimary}>
            Grabar Video
          </button>
        </div>
      )}

      {/* Mostrar cuenta regresiva y botón para detener grabación */}
      {isRecording && (
        <div style={{ marginTop: 20 }}>
          <p style={styles.countdown}>Tiempo restante: {countdown} s</p>
          <button onClick={stopRecording} style={styles.buttonDanger}>
            Detener Grabación
          </button>
        </div>
      )}

      {error && <p style={styles.error}>{error}</p>}

      {/* Mostrar barra de progreso mientras se sube el video */}
      {uploadProgress > 0 && uploadProgress < 100 && (
        <div style={styles.progressBarContainer}>
          <div style={{ ...styles.progressBar, width: `${uploadProgress}%` }}>
            {uploadProgress.toFixed(2)}%
          </div>
        </div>
      )}

      {/* Si el video ya se ha subido y se obtuvo la URL */}
      {downloadURL && (
        <div style={{ marginTop: 20 }}>
          {!videoSent ? (
            <>
              <p>Video subido:</p>
              <video src={downloadURL} controls style={styles.videoPreview} />
              <div style={styles.buttonGroup}>
                <button onClick={handleSend} style={styles.buttonPrimary}>
                  Enviar
                </button>
                <button onClick={handleDelete} style={styles.buttonDanger}>
                  Eliminar Video
                </button>
              </div>
            </>
          ) : (
            <>
              <p style={styles.successMessage}>
                Tu video será colocado en nuestra transmisión en vivo de YouTube.
              </p>
              <button
                onClick={() => (window.location.href = '/')}
                style={styles.buttonPrimary}
              >
                Volver al inicio
              </button>
            </>
          )}
        </div>
      )}

      {/* Si se seleccionó el video pero aún no se ha subido */}
      {videoFile && !downloadURL && !error && (
        <div style={{ marginTop: 10 }}>
          <p>Archivo seleccionado: {videoFile.name}</p>
          <button onClick={handleUpload} style={styles.buttonPrimary}>
            Subir Video
          </button>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: 20,
    textAlign: 'center',
    color: '#fff',
    // Imagen de fondo con overlay oscuro estilo Netflix/HBO
    background: "linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('https://i.pinimg.com/originals/b4/0e/96/b40e967b0f0afbacb94fb17681cd5dbb.jpg",
    backgroundSize: 'cover',
    minHeight: '100vh',
    fontFamily: 'Roboto, sans-serif',
  },
  title: {
    fontSize: '2.5em',
    marginBottom: '0.5em',
  },
  subtitle: {
    fontSize: '1.2em',
    marginBottom: 20,
  },
  buttonGroup: {
    marginBottom: 20,
  },
  buttonPrimary: {
    cursor: 'pointer',
    backgroundColor: '#e50914',
    color: '#fff',
    padding: '12px 24px',
    borderRadius: '4px',
    marginRight: '10px',
    border: 'none',
    fontWeight: 'bold',
    fontSize: '1em',
    transition: 'background-color 0.3s ease',
  },
  buttonSecondary: {
    cursor: 'pointer',
    backgroundColor: '#17a2b8',
    color: '#fff',
    padding: '10px 20px',
    borderRadius: '5px',
    marginRight: '10px',
    border: 'none',
  },
  buttonDanger: {
    cursor: 'pointer',
    backgroundColor: '#dc3545',
    color: '#fff',
    padding: '12px 24px',
    borderRadius: '4px',
    marginRight: '10px',
    border: 'none',
    fontWeight: 'bold',
    fontSize: '1em',
    transition: 'background-color 0.3s ease',
  },
  previewContainer: {
    marginTop: 20,
  },
  videoPreview: {
    maxWidth: '100%',
    borderRadius: '10px',
    marginBottom: 10,
  },
  countdown: {
    fontSize: '1.5em',
    fontWeight: 'bold',
  },
  error: {
    color: '#ff4d4d',
  },
  progressBarContainer: {
    width: '80%',
    height: '25px',
    backgroundColor: '#444',
    borderRadius: '5px',
    margin: '20px auto',
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#e50914',
    borderRadius: '5px',
    textAlign: 'center',
    lineHeight: '25px',
    color: '#fff',
  },
  filterGroup: {
    marginBottom: 10,
  },
  filterLabel: {
    marginRight: 10,
    fontSize: '1em',
  },
  select: {
    padding: '8px 12px',
    borderRadius: '4px',
    border: 'none',
    fontSize: '1em',
  },
  successMessage: {
    fontSize: '1.2em',
    fontWeight: 'bold',
    color: '#28a745',
  },
};

export default Uploadh;
